import { endpoints } from '@/services/endpoints';
import { axiosGetRequest } from '@/services/Request';
import { useQuery } from '@tanstack/react-query';
import { AppCard } from '@/components/ui/AppCard';
import { AppBarChart } from '@/components/charts/pieChart/AppBarChart';
import { AppPieChart } from '@/components/charts/pieChart/AppPieChart';
import { useMemo } from 'react';

export default function BookCharts() {
  // Books per category (API only returns counts, no labels)
  const { data: booksPerCategoryRaw, isLoading: catLoading } = useQuery({
    queryKey: [endpoints.getBooksPerCategory],
    queryFn: () => axiosGetRequest(endpoints.getBooksPerCategory),
    select: data => data.data || data.result,
  });

  // Fetch categories to get their names
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: [endpoints.getAllCategories],
    queryFn: () => axiosGetRequest(endpoints.getAllCategories),
    select: data => data.data || data.result,
  });

  // Merge category names with chart data
  const booksPerCategory = useMemo(() => {
    if (!booksPerCategoryRaw?.length) return [];
    return booksPerCategoryRaw.map((item: any, index: number) => ({
      ...item,
      category: categories?.[index]?.name_en || categories?.[index]?.name_ar || `Category ${index + 1}`,
    }));
  }, [booksPerCategoryRaw, categories]);

  // Books per month
  const { data: booksPerMonth, isLoading: monthLoading } = useQuery({
    queryKey: [endpoints.getBooksPerMonth],
    queryFn: () => axiosGetRequest(endpoints.getBooksPerMonth),
    select: data => data.data || data.result,
  });

  // Books status distribution
  const { data: booksStatusRaw, isLoading: statusLoading } = useQuery({
    queryKey: [endpoints.getBooksStatusDistribution],
    queryFn: () => axiosGetRequest(endpoints.getBooksStatusDistribution),
  });

  // Books by education type
  const { data: booksByEduType, isLoading: eduTypeLoading } = useQuery({
    queryKey: [endpoints.getBooksByEducationType],
    queryFn: () => axiosGetRequest(endpoints.getBooksByEducationType),
    select: data => data.data || data.result,
  });

  // Books by city
  const { data: booksByCity, isLoading: cityLoading } = useQuery({
    queryKey: [endpoints.getBooksByCity],
    queryFn: () => axiosGetRequest(endpoints.getBooksByCity),
    select: data => data.data || data.result,
  });

  // Books by education term (API returns 'type' field: first/second)
  const { data: booksByEduTerm, isLoading: eduTermLoading } = useQuery({
    queryKey: [endpoints.getBooksByEducationTerm],
    queryFn: () => axiosGetRequest(endpoints.getBooksByEducationTerm),
    select: data => data.data || data.result,
  });

  const barConfig = { count: { label: 'Count', color: '#0D9373' } };
  const pieConfig = {
    count: { label: 'Count', color: '#0D9373' },
    approved: { label: 'Approved', color: '#10B981' },
    pending: { label: 'Pending', color: '#F59E0B' },
    rejected: { label: 'Rejected', color: '#EF4444' },
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold text-coal-900">Book Analytics</h1>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Books per Category */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Books per Category</h4>
          <AppBarChart
            chartData={booksPerCategory || []}
            dataKey="count"
            nameKey="category"
            config={barConfig}
            isLoading={catLoading || categoriesLoading}
            className="h-80"
          />
        </AppCard>

        {/* Books per Month */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Books Added per Month</h4>
          <AppBarChart
            chartData={booksPerMonth || []}
            dataKey="count"
            nameKey="month"
            config={barConfig}
            isLoading={monthLoading}
            className="h-80"
          />
        </AppCard>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Books Status Distribution */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Books Status Distribution</h4>
          <AppPieChart
            chartData={booksStatusRaw?.data || booksStatusRaw?.result || []}
            dataKey="count"
            nameKey="status"
            config={pieConfig}
            innerData={{ value: booksStatusRaw?.totalCount || 'N/A', title: 'Total' }}
            isLoading={statusLoading}
          />
        </AppCard>

        {/* Books by Education Type */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Books by Education Type</h4>
          <AppBarChart
            chartData={booksByEduType || []}
            dataKey="count"
            nameKey="educationType"
            config={barConfig}
            isLoading={eduTypeLoading}
            className="h-80"
          />
        </AppCard>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {/* Books by City */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Books by City</h4>
          <AppBarChart
            chartData={booksByCity || []}
            dataKey="count"
            nameKey="city"
            config={barConfig}
            isLoading={cityLoading}
            className="h-80"
          />
        </AppCard>

        {/* Books by Education Term */}
        <AppCard>
          <h4 className="mb-4 text-base font-semibold text-coal-900">Books by Education Term</h4>
          <AppBarChart
            chartData={booksByEduTerm || []}
            dataKey="count"
            nameKey="type"
            config={barConfig}
            isLoading={eduTermLoading}
            className="h-80"
          />
        </AppCard>
      </div>
    </div>
  );
}
